import { useCallback, useEffect, useMemo, useState } from 'react'
import { MapPin, Play } from 'lucide-react'
import { SectionHeader } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { Modal } from '@/shared/ui/Modal'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { listCoverageEvents, listCoverageRecordings } from './api'
import { CoverageReplayPlayer, fmtBytes, fmtClock } from './players'
import { CoveragePoster } from './Poster'
import { classificationLabel, isSensitive, placeOf, recordingSourceLabel } from './vocab'
import type { CoverageEventResponse, CoverageRecordingResponse } from '@/shared/api/types'

/**
 * Everything that was recorded.
 *
 * The archive is assembled from the ENDED events rather than from a global
 * recording list, because a recording on its own is a file — what makes it
 * findable a month later is the rally it came from and where that was.
 */
export function CoverageArchivePage() {
  const canRead = useCan('coverage:read')
  const canWatch = useCan('coverage:watch')
  const [playing, setPlaying] = useState<{ rec: CoverageRecordingResponse; event: CoverageEventResponse } | null>(null)

  // 60 events used to mean 60 parallel recording requests on mount. Kept as a
  // page-level cap with a per-group fetch, but the cap is now the thing that
  // bounds it, and it is stated rather than implied.
  const PAGE = 24
  const { data, error, loading } = useLoad(
    () => listCoverageEvents({ status: 'ENDED' }, 0, PAGE), [],
  )
  const events = useMemo(() => data?.content ?? [], [data])

  const [rendered, setRendered] = useState<Set<string>>(new Set())
  const [reported, setReported] = useState(0)
  const noteRendered = useCallback((id: string, has: boolean) => {
    setReported((n) => n + 1)
    setRendered((prev) => {
      if (has === prev.has(id)) return prev
      const next = new Set(prev)
      if (has) next.add(id); else next.delete(id)
      return next
    })
  }, [])
  const settled = events.length > 0 && reported >= events.length

  if (!canRead) return <Forbidden what="live coverage" />

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Archive"
        description="Finished coverage, kept as recordings you can review afterwards."
      />
      <ErrorStrip message={error} />
      {/* Stated as text rather than as a `title` on a disabled control — see LiveNowPage. */}
      {!canWatch && (
        <Note tone="info">
          You can see what was recorded. Playing a recording needs the watch permission,
          which you do not hold.
        </Note>
      )}

      {loading && events.length === 0 ? <LoadingBlock label="Loading the archive…" />
        : events.length === 0 ? (
          <EmptyState
            title="The archive is empty"
            hint="Every broadcast is recorded by the media server as it goes out. The first one you run appears here."
          />
        ) : (
          <div className="space-y-4">
            {events.map((e) => (
              <ArchiveGroup
                key={e.id}
                event={e}
                canWatch={canWatch}
                onPlay={(rec) => setPlaying({ rec, event: e })}
                onRendered={noteRendered}
              />
            ))}
            {/*
              Every group can legitimately render nothing, which left a heading
              over blank space: the empty state was gated on how many ENDED events
              exist, not on whether any of them produced anything.
            */}
            {settled && rendered.size === 0 && (
              <EmptyState
                title="Nothing has been recorded yet"
                hint="These events finished without a recording. A camera taken live is recorded by the media server as it goes out."
              />
            )}
          </div>
        )}

      {playing && (
        <Modal
          open
          onClose={() => setPlaying(null)}
          title={playing.event.title}
          width="lg"
        >
          <div className="space-y-2">
            <div className="text-erp-xs text-muted-foreground">
              {recordingSourceLabel(playing.rec.sourceCode)} · {placeOf(playing.event)}
              {playing.rec.endedAt && ` · ${new Date(playing.rec.endedAt).toLocaleString()}`}
            </div>
            <CoverageReplayPlayer
              recordingId={playing.rec.id}
              stateCode={playing.rec.stateCode}
              className="aspect-video w-full"
            />
          </div>
        </Modal>
      )}
    </div>
  )
}

function ArchiveGroup({
  event, canWatch, onPlay, onRendered,
}: {
  event: CoverageEventResponse
  canWatch: boolean
  onPlay: (r: CoverageRecordingResponse) => void
  onRendered: (eventId: string, rendered: boolean) => void
}) {
  const { data, loading } = useLoad(() => listCoverageRecordings(event.id), [event.id])
  const all = data ?? []
  const ready = all.filter((r) => r.stateCode === 'READY')
  // A recording still being written is NOT nothing, and hiding it is the silent
  // gap this panel exists to close: somebody who stopped a broadcast a minute ago
  // must see that the archive knows about it, or they will file a bug.
  const inFlight = all.filter((r) => r.stateCode === 'PENDING' || r.stateCode === 'RECORDING')
  const failed = all.filter((r) => r.stateCode === 'FAILED')

  useEffect(() => {
    onRendered(event.id, ready.length + inFlight.length + failed.length > 0)
  }, [event.id, ready.length, inFlight.length, failed.length, onRendered])

  if (loading) return null
  if (ready.length === 0 && failed.length === 0 && inFlight.length === 0) return null

  return (
    <section className="space-y-2">
      <header className="flex flex-wrap items-baseline gap-2">
        <h2 className="text-erp-sm font-semibold">{event.title}</h2>
        {isSensitive(event.classificationCode) && (
          <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-erp-xs font-medium text-amber-700 dark:text-amber-400">
            {classificationLabel(event.classificationCode)}
          </span>
        )}
        <span className="flex items-center gap-1 text-erp-xs text-muted-foreground">
          <MapPin className="h-3 w-3" /> {placeOf(event)}
        </span>
      </header>

      {inFlight.length > 0 && (
        <Note tone="info">
          {inFlight.length === 1 ? 'A recording is' : `${inFlight.length} recordings are`} still
          being written. {inFlight.some((r) => r.stateCode === 'PENDING')
            ? 'One has been requested and has not confirmed yet.' : ''}
        </Note>
      )}

      {failed.length > 0 && ready.length === 0 && (
        <Note tone="bad">
          This event was covered but nothing was recorded — {failed[0]?.error ?? 'the recording failed'}.
        </Note>
      )}

      {ready.length > 0 && (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
          {ready.map((r) => (
            <button
              key={r.id}
              type="button"
              className="erp-card overflow-hidden text-left transition hover:border-chocolate/50 disabled:opacity-60"
              disabled={!canWatch}
              onClick={() => onPlay(r)}
            >
              <div className="relative aspect-video">
                <CoveragePoster
                  recordingId={r.id}
                  hasPoster={r.posterFileId != null}
                  kind="film"
                  className="h-full w-full"
                />
                {r.durationSec != null && (
                  <span className="absolute bottom-2 right-2 rounded bg-black/70 px-1.5 py-0.5 text-erp-xs tabular-nums text-white">
                    {fmtClock(r.durationSec)}
                  </span>
                )}
                {canWatch && (
                  <span className="absolute inset-0 grid place-items-center opacity-0 transition hover:opacity-100">
                    <Play className="h-8 w-8 text-white drop-shadow" />
                  </span>
                )}
              </div>
              <div className="space-y-0.5 p-2.5 text-erp-xs">
                <div className="font-medium">{recordingSourceLabel(r.sourceCode)}</div>
                <div className="text-muted-foreground">
                  {r.endedAt ? new Date(r.endedAt).toLocaleString() : '—'} · {fmtBytes(r.sizeBytes)}
                </div>
                {r.capturedByName && (
                  <div className="text-muted-foreground">{r.capturedByName}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  )
}
